/**
 * JourneyRouter.tsx
 *
 * A catch-all route renderer for the V3 Journey Builder engine.
 *
 * Mounted at /journey/:slug in App.tsx. When a user navigates to a dynamic
 * page route (e.g. /journey/personal-info), this component:
 *   1. Fetches the current page from /api/journey/current-page/
 *   2. Verifies the slug matches (redirects if not)
 *   3. Renders DynamicPage
 *
 * This is the V3 equivalent of the individual route entries in App.tsx
 * (e.g. <Route path="/personal-info" element={<PersonalInfo />} />).
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import DynamicPage from "@/components/DynamicPage";
import FullScreenLoader from "@/components/FullScreenLoader";

interface CurrentPageResponse {
  id: number;
  slug: string;
  route: string;
  step_index: number;
  total_steps: number;
  journey_config_id: number;
}

const JourneyRouter: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    if (!user) {
      setChecking(false);
      return;
    }

    // Verify the slug matches the user's current journey page
    fetch("/api/journey/current-page/", { credentials: "include" })
      .then(async (res) => {
        if (res.status === 204 || res.status === 404) {
          // Journey complete or no builder_app journey — redirect to legacy home
          navigate("/", { replace: true });
          return;
        }
        if (!res.ok) {
          navigate("/", { replace: true });
          return;
        }
        const page: CurrentPageResponse = await res.json();

        // Allow access if slug matches OR if the page's canonical route matches
        const slugMatches = page.slug === slug;
        const routeMatches = page.route === `/journey/${slug}`;

        if (slugMatches || routeMatches) {
          setAuthorized(true);
        } else {
          // Wrong slug — redirect to the correct page
          console.warn(
            `[JourneyRouter] Slug '${slug}' does not match current page '${page.slug}'. Redirecting.`
          );
          navigate(page.route, { replace: true });
        }
      })
      .catch(() => {
        navigate("/", { replace: true });
      })
      .finally(() => {
        setChecking(false);
      });
  }, [user, slug, navigate]);

  if (checking) {
    return <FullScreenLoader loading={true} />;
  }

  if (!authorized) {
    return null;
  }

  return <DynamicPage />;
};

export default JourneyRouter;
